import PropTypes from 'prop-types';
import React, { Component, Fragment } from 'react';
import { connect } from 'react-redux';
import cx from 'classnames/bind';
import Immutable from 'immutable';

import { resultsConfigSelector } from 'ctm-selectors';
import { get, getRulesFromState, haveRulesChanged, heightFix, sortAndMutatePdsData } from 'ctm-util-functions';

import { HelpIndicator } from '../../../../../../form/help_indicator/help_indicator';
import { Icon } from '../../../../../../icon/icon';
import { Button } from '../../../../../../button/button';
import { Text } from '../../../../../../text/text';
import { TextTemplate } from '../../../../../../text_template/text_template';
import { ModalContainer } from '../../../../../../modal/modal_container';

import { toggleAccordionRow } from '../../../../../actions';

import styles from './features_cell.css';
import { getUnderwriterDetails } from '../../../../../../utils';

const classNames = cx.bind(styles);

/**
 * The features cell renderer component.
 * This component is used to render the cell for features in the grid.
 *
 * @class _FeaturesCell
 * @extends {Component}
 * @param {Object} content - The content to render inside the cell.
 */
class _FeaturesCell extends Component {

    /**
     * Remove whitespace and convert string to camelCase
     * @param  {String} label  The incoming string
     * @returns {String} Modified label string
     */
    convertToCamelCase = (label) => {
        return label.replace(/(?:^\w|[A-Z]|\b\w)/g, (word, index) => {
            return index === 0 ? word.toLowerCase() : word.toUpperCase();
        }).replace(/\s+/g, '');
    };

    /**
     * Determine if the component should re-render based on the nextState
     * @param  {Object} nextProps  The incoming set of props
     * @returns {Boolean} true if the component should re-render
     */
    shouldComponentUpdate = (nextProps) => {
        const { accordionOpenState } = this.props;
        return haveRulesChanged(nextProps, this.props) || nextProps.accordionOpenState !== accordionOpenState;
    };

    /**
     * Sanitises a string item to ensure it is a currency value
     * @param  {String} item  The currency value to sanitise
     * @param  {Number} index  The index of the item (defaults to 1)
     * @returns {String} returns sanitised currency value
     */
    sanitiseCurrencyValue = (item, index = 1) => {
        if (index !== 1) return item;

        return `${item}`.indexOf('$') !== -1 ? item : `$${item}`;
    };

    /**
     * Render a features cell with an icon
     * @param {String} content - the content to be display in the cell
     * @returns {Node} renders the correct icon for the content type
     *
     */
    renderIconCell = (content) => {
        switch (content) {
            case 'Y':
                return <Icon variant="ctm_tick" color="brand_secondary" />;
            case 'N':
            case null:
                return <Icon variant="ctm_cross" color="feature_3" />;
            case 'O':
                return <>Optional</>;
            case 'R':
                return <>Restricted</>;
            default:
                return content;
        }
    };

    /**
     * a feature cell label, which can be a standard feature label or full width label
     * @param {String} cellContent - the content to be display in the cell
     * @returns {Node} a cell label
     */
    renderLabelCell = (cellContent) => {
        const { label, accordion, accordionOpenState, helpText, content } = this.props;
        const accordionLabelStyle = accordionOpenState ? 'accordionLabelOpen' : 'accordionLabelClosed';
        return (
        // if the labelCell isn't a true label - it must be a multiline row label, so apply the hidden label style
        // which matches the font color to the background and removes border right

            <div className={classNames('feature_view_label', { [styles.hiddenLabel]: !label })}>
                {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
                <span className={classNames({ [styles[accordionLabelStyle]]: accordion })} onClick={accordion ? this.onClick : null}>{cellContent}</span>
                        &nbsp;
                {helpText
                    ? (
                        <HelpIndicator
                            contentProps={{
                                content: helpText,
                                format: 'html',
                                label: `tooltipFor_${this.convertToCamelCase(content)}` }}
                            schema={{ identifier: 'inline' }}
                        />
                    )
                    : ''}
            </div>
        );
    };

    /**
     * Render a full width label (spanning the full width of the results container) before a feature content cell, usually above.
     * @param {String} cellContent - the full with label, label
     * @returns {Node} the full width label
     */
    renderFullWidthLabel = (cellContent) => {
        const { numberOfQuotes, columnsPerPage, helpText, content } = this.props;
        return (
            <span className={styles[`fullWidthLabel_${numberOfQuotes}_${numberOfQuotes}`]}>
                {this.renderIconCell(cellContent)}
                        &nbsp;
                {helpText
                    ? (
                        <HelpIndicator
                            className={'fullWidthHelpText'}
                            contentProps={{ content: helpText, format: 'html', label: `tooltipFor_${this.convertToCamelCase(content)}` }}
                            schema={{ identifier: 'inline' }}
                            placement="top" />
                    )
                    : ''}
            </span>

        );
    };

    /**
     * @todo could use the price component here?
     * Render the content with appropriate currency styling
     * @param {String} content - the content to be display in the cell
     * @returns {Node} a cell with currency content
     */
    renderCurrencyCell = (content) => {
        if (content === '0') {
            return (<><p style={{ marginBottom: 0 }}>N/A</p></>);
        }
        return (
            <>
                $
                {content}
            </>
        );
    };

    /**
     * a cell with formatted HTML List
     * @param {String} content - the content to be display in the cell
     * @returns {Node} - a cell with formatted HTML List
     */
    renderHTMLListCell = (content) => {
        let cellContent = !content ? '' : content;
        const { defaultValue = '' } = this.props;
        // Convert single string to a html List for Text Template - to support Car/HNC special offer
        if (!cellContent.includes('<ul>')) {
            cellContent = `<ul><li>${content}</li></ul>`;
        }

        // strip out the default H5 title we receive from the back end
        // @todo at the moment the html cell is completely removing H5 tags from the content, need to revisit this at some stage
        // depending on requirements from future verticals. We've discussed perhaps supplying a regex within the schema config
        if (cellContent.includes('<h5>')) {
            cellContent = cellContent.replace(/(<h5\b[^>]*>)[^<>]*(<\/h5>)/i, '');
        }

        return content ? <TextTemplate innerHTML={cellContent} /> : <Text variant="flex_center" label={defaultValue} />;
    };

    /**
     * @todo "Part A" and "Part B" is specific to HNC/Car, so we may need to render different types of buttons.
     * Possibly then we should include some other 'config button type' data in features_details.json
     * Renders buttons within a cell
     * @param {String} content - the content to be display in the cell
     * @returns {Node} - a cell with buttons
     */
    renderPdsCell = (content) => {
        if (!content) return null;
        let pdsContent = content;
        // Override for Car
        if (Immutable.List.isList(pdsContent)) {
            pdsContent = sortAndMutatePdsData(pdsContent, 'part');
        } else {
            pdsContent = pdsContent.filter((pds) => pds.length > 0);
            pdsContent = Immutable.fromJS(
                pdsContent.map((pds, index) => {
                    const char = String.fromCharCode(65 + index);
                    return {
                        url: pds,
                        title: pdsContent.length > 1 ? `Part ${char}` : 'Product Disclosure Statement',
                        key: `pds.${char}`
                    };
                })
            );
        }

        const hasSpds = pdsContent.find((pds) => pds.get('code') === 'spds');
        if (hasSpds) {
            pdsContent = pdsContent.filter((pds) => pds.get('code') !== 'ped');
        }

        return pdsContent.map((pds) => (
            <div key={pds.get('key')} className={styles.pdsButton}>
                <Button UIComponent="btn_primary_transparent_small_semi_rounded" variant="full_width" href={pds.get('url')} target="_blank">
                    {pds.get('title')}
                </Button>
            </div>
        ));
    };

    /**
     * Renders a Immutable list, as key value pairs, within a list element
     * @param {Object} content - an immutable list of key : value pairs
     * @returns {Node} an unordered list of key value pairs
     */
    renderArrayToListCell = (content) => {
        // @todo clean this up so this function only recieves a list object, not a list within an array
        return content[0] && content[0].size ? (
            <ul>
                {content.map((key) => key.map((value, i) => (
                    <li key={`item_${i + 1}`} className={styles.arrayToListItem}>
                        {value.toArray().map((item, j) => (
                            <span key={`value_${j + 1}`}>{this.sanitiseCurrencyValue(item, j)}</span>
                        ))}
                    </li>
                ))
                )}
            </ul>
        ) : (
            <>Refer to PDS</>
        );
    };

    /**
     * Render a combined cell for underwriter
     * @param {List} content - the content to be display in the cell
     * @returns {Node} a cell with the underwriter name, ACN and AFSL number
     */

    renderUnderwriterDetailsCell = (content) => {
        const underwriterDetails = getUnderwriterDetails(content);
        return (
            <>
                {underwriterDetails.name}
                <br />
                {`ACN ${underwriterDetails.acn}`}
                <br />
                {`AFSL ${underwriterDetails.afsLicenseNo}`}
                <br />
            </>
        );
    };

    /**
     * Render a combined icon with content within a cell
     * The format of the data will be {val}{content} - val being the icon value and content the extra content
     * @param {List} content - the content to be display in the cell
     * @returns {Node} a cell with an icon and extra details
     */
    renderCombinedCell = (content) => {
        return (
            <>
                {this.renderIconCell(content[0])}
                <br />
                {content[1]}
            </>
        );
    };

    /**
     * Render a combined text with modal text within a cell
     * @param {String} content - the content to be display in the modal
     * @returns {Node} a cell with content and text that opens a modal
     */
    renderTextWithModalCell = (content) => {
        const { modalProps, defaultValue = '' } = this.props;
        return content.length > 0 ? (
            <>
                {this.renderHTMLListCell(content[0])}
                &nbsp;
                {content[1] && (
                    <ModalContainer {...modalProps}>
                        <TextTemplate innerHTML={content[1]} />
                    </ModalContainer>
                )}
            </>
        ) : (
            <Text variant="flex_center" label={defaultValue} />
        );
    };

    /**
     * Renders a key value pair list of elements from an immutable map
     * @param {Immutable.Map} content - the map of values to render
     * @returns {Node} list of keys and their values
     */
    renderObjectToListCell = (content) => {
        const list = [];
        content.entrySeq().forEach((e) => {
            list.push(
                <div className={styles.objectListItem}>
                    <span>{e[0]}</span>
                    <span>{e[1]}</span>
                </div>
            );
        });
        return list;
    };

    /**
     * Renders a key value list of elements from the customItems prop
     * @param {Array} content - Array of objects consisting of a description and stateKey for which to obtain the amount from.
     * @returns {Node} Unordered list of key/value pairs (description, formattedAmount)
     */
    renderCustomCell = (content) => {
        return (
            <div className={styles.content}>
                {content.length > 0 ? (
                    <ul>
                        {content.map((item, i) => (
                            <li key={`item_${i + 1}`} className={styles.arrayToListItem}>
                                <span key="value_0">{item.description}</span>
                                <span key="value_1">{this.sanitiseCurrencyValue(item.amount)}</span>
                            </li>
                        ))}
                    </ul>
                ) : (
                    <>Refer to PDS</>
                )}
            </div>
        );
    };

    /**
     * Renders a closed or open accordion cell based on state
     * @param {String} content - the content to be displayed in the open state of the cell
     * @returns {Node} a cell with closed and open state, with appropriate content
     */
    renderAccordionCell = (content) => {
        const { accordionOpenState, accordionLabel, accordion } = this.props;
        heightFix('accordion');
        return accordionOpenState ? (
            // eslint-disable-next-line jsx-a11y/no-static-element-interactions
            <div onClick={accordion ? this.onClick : null}>
                <div className={styles.accordionCellOpen}>{accordionLabel}</div>
                {content}
            </div>
        ) : (
            // eslint-disable-next-line jsx-a11y/no-static-element-interactions
            <div className={styles.accordionCellClosed} onClick={accordion ? this.onClick : null}>{accordionLabel}</div>
        );
    };

    /**
     * Renders a TMD cell with a link.
     * @param {Array<string>} content - The content to be displayed in the modal (expected to be an array of URLs).
     * @returns {Node|null} A React element containing the TMD link, or null if content is not a valid array or is empty.
     */
    renderTmdCell = (content) => {
        if (!Array.isArray(content) || content.length === 0) return null;

        return (
            <div key={content[0]} className={styles.tmdButton}>
                <Button UIComponent="btn_primary_transparent_small_semi_rounded" variant="full_width" href={content[0]} target="_blank">
                    {'TMD'}
                </Button>
            </div>
        );
    };

    /**
     * Dispatch the toggle accordion row action on cell click
     */
    onClick = () => {
        const { dispatch, id, accordionOpenState } = this.props;
        dispatch(toggleAccordionRow(id, !accordionOpenState));
    };

    /**
     * for the given format, determine which cell type to render
     * @param {String} format - the cell format
     * @param {String} content - the cell content
     * @returns {Node} the format's cell type with content
     */
    renderCell = (format, content) => {
        switch (format) {
            case 'labelCell':
                return this.renderLabelCell(content);
            case 'fullWidthLabel':
                return this.renderFullWidthLabel(content);
            case 'currency':
                return this.renderCurrencyCell(content);
            case 'html':
                return this.renderHTMLListCell(content);
            case 'pds':
                return this.renderPdsCell(content);
            // case 'button':
            //     return this.renderButtonCell(content);
            case 'arrayToList':
                return this.renderArrayToListCell(content);
            case 'objectToList':
                return this.renderObjectToListCell(content);
            case 'underwriterDetails':
                return this.renderUnderwriterDetailsCell(content);
            case 'combined':
                return this.renderCombinedCell(content);
            case 'custom':
                return this.renderCustomCell(content);
            case 'textWithModal':
                return this.renderTextWithModalCell(content);
            case 'tmd':
                return this.renderTmdCell(content);
            default:
                return this.renderIconCell(content);
        }
    };

    /**
     * Render the features mode results
     * @returns {Node} the configured features mode columns
     */
    render = () => {
        const { id, className, rules, format, balanceHeight, content, label, accordion, lastCell } = this.props;
        // rules evaluation, defaults to true as not all features have rules
        if (get(rules, 'visibility')) {
            if (!rules.visibility.result) {
                return null;
            }
        }
        const cellContent = this.renderCell(format, content);
        // apply variant classes and the data-multiRow attr if multiRow has been specified
        return (
            <div
                role="none"
                className={classNames(styles[className], { [styles.accordion]: accordion && label }, { [styles.lastCell]: lastCell })}
                data-balanceheight={balanceHeight ? id : null}
                data-accordion={accordion ? id : null}
            >
                {accordion && !label ? this.renderAccordionCell(cellContent) : cellContent}
            </div>

        );
    };

}

_FeaturesCell.propTypes = {
    /* an id used to specify multiRows */
    id: PropTypes.string,
    /* the cell class name, usually depending on it's format */
    className: PropTypes.string,
    /* a rules object definition */
    rules: PropTypes.object,
    /* the cell format */
    format: PropTypes.string,
    /* whether the cell is a multiRow or not */
    balanceHeight: PropTypes.bool,
    helpText: PropTypes.string,
    /* the content of the cell */
    content: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.array, PropTypes.instanceOf(Immutable.List)]),
    /* whether the cell is a standard cell or a label */
    label: PropTypes.bool,
    /* whether the cell is a last cell */
    lastCell: PropTypes.bool,
    dispatch: PropTypes.func,
    accordionOpenState: PropTypes.bool,
    accordion: PropTypes.bool,
    accordionLabel: PropTypes.string,
    numberOfQuotes: PropTypes.number,
    columnsPerPage: PropTypes.number,
    defaultValue: PropTypes.string,
    modalProps: PropTypes.shape({
        openComponent: PropTypes.array
    })
};

_FeaturesCell.defaultProps = {
    className: 'content'
};
/**
 * Evaluate the rules for each cell element - needed to do at this level as the labels / cells are physically separated
 * within their own columns
 * @param {Object} state - the application state
 * @param {Object} props - the component's props
 * @returns {{rules: *}} the rules after evaluation
 */
const mapStateToProps = (state, props) => {
    const accordionOpenState = resultsConfigSelector(state).getIn(['featuresModeAccordionOpenState', props.id]) || false;
    return {
        accordionOpenState,
        rules: getRulesFromState(state, props)
    };
};

const FeaturesCell = connect(mapStateToProps)(_FeaturesCell);
export { _FeaturesCell, FeaturesCell };
